import React from "react";
import { useQuery } from "@apollo/client";
import {
  Box,
  Container,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CAMPAIGNS_LISTING_QUERY } from "../graphql/campaigns";
import { buildImageUrl } from "../config";

type Campaign = {
  id: string;
  name: string;
  images: string[];
};

type GetCampaignsData = {
  campaignsListing: Campaign[];
};

const CampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery<GetCampaignsData>(
    CAMPAIGNS_LISTING_QUERY,
    { variables: { limitPerCampaign: 3 } },
  );

  const campaigns = data?.campaignsListing ?? [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          backgroundColor: "#F5F5F5",
        }}
      >
        <Container maxWidth="lg">
          {/* Page heading */}
          <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: "Playfair Display, serif",
                fontWeight: 500,
                letterSpacing: 0.4,
                fontSize: { xs: 30, md: 44 },
                textTransform: "uppercase",
                color: "#111",
              }}
            >
              Campaigns Collection
            </Typography>

            <Typography
              sx={{
                mt: 1.5,
                color: "black",
                maxWidth: 720,
                mx: "auto",
                fontSize: { xs: ".95rem", md: "1.05rem" },
              }}
            >
              Discover signature stories from Haroon Sharif Jewellers each
              campaign captures a distinct mood, styled look, and the timeless
              glow of our jewelry.
            </Typography>
          </Box>

          {/* Loader / Error / Empty states */}
          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              Unable to load campaigns at the moment. Please try again shortly.
            </Alert>
          )}

          {!loading && !error && campaigns.length === 0 && (
            <Typography
              sx={{
                textAlign: "center",
                color: "text.secondary",
                py: 6,
              }}
            >
              No campaigns are available yet. Please check back soon.
            </Typography>
          )}

          {/* Campaign list */}
          {!loading &&
            !error &&
            campaigns.map((campaign, idx) => {
              const images = campaign.images || [];
              const previewImages = isMobile
                ? images.slice(0, 2)
                : images.slice(0, 3);

              return (
                <Box
                  key={campaign.id ?? `${campaign.name}-${idx}`}
                  sx={{
                    mb: { xs: 6, md: 8 },
                    px: { xs: 2, md: 4 },
                    py: { xs: 3, md: 4 },
                    borderRadius: 0,
                  }}
                >
                  {/* Campaign heading */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                      mb: { xs: 2.5, md: 3 },
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      component="h2"
                      sx={{
                        fontFamily: `"Bodoni Moda", serif`,
                        fontWeight: 600,
                        fontSize: { xs: 22, md: 28 },
                        letterSpacing: 0.3,
                        color: "#111",
                        textTransform: "uppercase",
                      }}
                    >
                      {campaign.name}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: { xs: ".8rem", md: ".85rem" },
                        letterSpacing: 1.6,
                        textTransform: "uppercase",
                        color: "text.secondary",
                      }}
                    >
                      Campaign #{idx + 1}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: { xs: 2.5, md: 3.5 } }} />

                  {/* Images grid (max 4) */}
                  <Grid container spacing={{ xs: 1.5, md: 3 }}>
                    {previewImages.map((src, imageIndex) => (
                      <Grid
                        item
                        xs={6}
                        sm={4}
                        md={4}
                        key={`${campaign.id}-img-${imageIndex}`}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            paddingTop: "100%",
                            borderRadius: "10px",
                            overflow: "hidden",
                            backgroundColor: "#f6f6f6",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.10)",
                          }}
                        >
                          <Box
                            component="img"
                            loading="lazy"
                            decoding="async"
                            src={buildImageUrl(src)}
                            alt={`${campaign.name} – image ${imageIndex + 1}`}
                            sx={{
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              objectPosition: "center",
                            }}
                          />
                        </Box>
                      </Grid>
                    ))}

                    {/* If no images for that campaign */}
                    {images.length === 0 && (
                      <Grid item xs={12}>
                        <Typography
                          sx={{
                            textAlign: "center",
                            color: "text.secondary",
                            fontSize: ".9rem",
                          }}
                        >
                          Images for this campaign will be added soon.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  {/* Explore More button if there are more than 3 images */}
                  {images.length > 2 && (
                    <Box sx={{ textAlign: "center", mt: 3 }}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate(`/campaigns/${campaign.id}`)}
                        sx={{
                          px: 4,
                          py: 1.1,
                          textTransform: "uppercase",
                          letterSpacing: 1.6,
                          borderRadius: 0,
                          borderColor: "#111",
                          color: "#111",
                          "&:hover": {
                            backgroundColor: "#111",
                            color: "#fff",
                            borderColor: "#111",
                          },
                        }}
                      >
                        Explore More
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })}
        </Container>
      </Box>
    </>
  );
};

export default CampaignsPage;
